import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Descriptions, Modal, Space, Typography, message } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { UserAccountWithTemporaryPassword } from '../../api/userAccounts';

/** Tham số của hộp hiện mật khẩu tạm. */
export interface TemporaryPasswordModalProps {
  /**
   * Kết quả vừa nhận từ máy chủ, hoặc rỗng để đóng hộp.
   */
  readonly result: UserAccountWithTemporaryPassword | null;

  /** Đóng hộp. */
  readonly onClose: () => void;
}

/**
 * Hộp hiện mật khẩu tạm sau khi lập tài khoản hoặc đặt lại mật khẩu.
 *
 * **Đây là chỗ dễ gây thiệt hại thật nhất của cả màn hình quản trị tài khoản.** Mật khẩu tạm chỉ hiện
 * đúng một lần: máy chủ chỉ lưu chuỗi băm, nên đóng hộp mà chưa ghi lại thì cách duy nhất là đặt lại
 * mật khẩu lần nữa — và với người dùng đang chờ nhận tài khoản thì đó là một vòng chờ thêm.
 *
 * Ba quyết định của hộp này đều xuất phát từ điều đó:
 *
 * 1. **Không đóng được bằng cách bấm ra ngoài hay bấm dấu nhân.** Chỉ có đúng một nút đóng, và nhãn
 *    của nó là một lời xác nhận: *"Tôi đã lưu lại mật khẩu"*. Một hộp đóng được bằng cách bấm nhầm ra
 *    ngoài sẽ có ngày bị đóng nhầm.
 * 2. **Mật khẩu hiện dưới dạng chữ chọn được, không phải ô nhập bị che.** Người quản trị cần đọc và
 *    sao chép nó, không cần che — che một chuỗi rồi bắt họ bấm hiện là thêm một bước vô ích.
 * 3. **Nút sao chép có nhưng không bắt buộc.** Trình duyệt chỉ cho phép ghi vào bộ nhớ tạm ở ngữ cảnh
 *    an toàn, và hệ thống có thể chạy trên HTTP trong mạng nội bộ. Nếu sao chép thất bại thì chuỗi vẫn
 *    nằm đó để chọn bằng tay.
 */
export function TemporaryPasswordModal({
  result,
  onClose,
}: TemporaryPasswordModalProps): ReactNode {
  const [isCopying, setIsCopying] = useState(false);

  async function copyAsync(): Promise<void> {
    if (result === null) {
      return;
    }

    setIsCopying(true);

    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      void message.success('Đã sao chép mật khẩu tạm.');
    } catch {
      // Ghi vào bộ nhớ tạm chỉ chạy được ở ngữ cảnh an toàn — HTTPS, hoặc localhost. Hệ thống có thể
      // chạy trên HTTP trong mạng nội bộ, nên đây là trường hợp bình thường chứ không phải lỗi.
      void message.warning(
        'Trình duyệt không cho sao chép tự động. Hãy chọn chuỗi mật khẩu rồi sao chép bằng tay.',
      );
    } finally {
      setIsCopying(false);
    }
  }

  return (
    <Modal
      title="Mật khẩu tạm — chỉ hiện một lần"
      open={result !== null}
      maskClosable={false}
      closable={false}
      keyboard={false}
      footer={
        <Button type="primary" onClick={onClose}>
          Tôi đã lưu lại mật khẩu
        </Button>
      }
      onCancel={onClose}
      destroyOnHidden
    >
      {result !== null && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Ghi lại ngay trước khi đóng"
            description={
              'Hệ thống chỉ lưu chuỗi băm của mật khẩu, nên chuỗi dưới đây không lấy lại được. '
              + 'Đóng hộp này mà chưa ghi lại thì phải đặt lại mật khẩu một lần nữa.'
            }
          />

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Tên đăng nhập">
              <Typography.Text copyable strong>
                {result.account.userName}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label="Họ tên">{result.account.fullName}</Descriptions.Item>

            <Descriptions.Item label="Mật khẩu tạm">
              <Typography.Text
                code
                strong
                style={{ fontSize: 16, letterSpacing: 1, userSelect: 'all' }}
              >
                {result.temporaryPassword}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>

          <Button
            icon={<CopyOutlined />}
            loading={isCopying}
            onClick={() => {
              void copyAsync();
            }}
          >
            Sao chép mật khẩu
          </Button>

          <Alert
            type="info"
            showIcon
            message="Người dùng bị buộc đổi mật khẩu ở lần đăng nhập đầu tiên"
            description={
              'Mật khẩu tạm chỉ dùng được một lần để vào hệ thống. Gửi nó qua kênh riêng — tin nhắn '
              + 'trực tiếp hoặc nói trực tiếp — chứ không gửi chung một tin cho nhiều người.'
            }
          />
        </Space>
      )}
    </Modal>
  );
}
